import Divider from 'components/Divider';
import SelectInput from 'components/shared/SelectInput';
import {
  EPreconversationType,
  ITavusPersonaFormik
} from 'entities/tavus/persona';
import { FieldArray, FormikProps, FormikProvider } from 'formik';
import { AddCircle, MinusCirlce, Trash } from 'iconsax-reactjs';
import { FC, useState } from 'react';
import { FiPlus } from 'react-icons/fi';
import { FilledButton } from 'shared/ui';
import FormInput from 'shared/ui/FormInput';

interface IProps {
  formik: FormikProps<ITavusPersonaFormik>;
}

export const PreconversationEditForm: FC<IProps> = ({ formik }) => {
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>(
    () =>
      formik.values.preconversationConfig?.fields.reduce(
        (acc, _, index) => ({ ...acc, [index]: true }),
        {}
      )
  );

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const generateNameFromLabel = (label: string): string => {
    return label
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^A-Za-zА-Яа-я_]/g, '')
      .replace(/_{2,}/g, '_');
  };

  return (
    <div className="ag-space-y-6 ag-text-neutral-900 ag-h-full">
      <FormikProvider value={formik}>
        <FieldArray name="preconversationConfig.fields">
          {({ push, remove }) => (
            <>
              <div className="ag-flex ag-flex-row ag-items-center ag-justify-between">
                <div className="ag-font-semibold ag-text-base">Create Form</div>
                <FilledButton
                  type="button"
                  className="ag-text-white ag-bg-purple-500 hover:ag-bg-purple-600 ag-font-medium ag-text-sm ag-px-4 ag-py-2 ag-rounded-lg ag-flex ag-flex-row ag-items-center ag-gap-1"
                  onClick={() => {
                    push({
                      name: '',
                      type: EPreconversationType.TEXT,
                      label: 'Untitled'
                    });
                    setExpandedCards((prev) => ({
                      ...prev,
                      [formik.values.preconversationConfig.fields.length]: true
                    }));
                  }}
                >
                  <FiPlus size={16} />
                  Add New Field
                </FilledButton>
              </div>

              <div className="ag-space-y-4 ag-overflow-auto ag-h-full ag-max-h-[calc(100%-54px)]">
                {formik.values.preconversationConfig?.fields.map(
                  (field, index) => (
                    <div
                      key={index}
                      className="ag-border ag-border-neutral-300 ag-rounded-lg ag-p-4 ag-bg-white ag-select-none"
                    >
                      <div className="ag-flex ag-justify-between ag-items-center ag-font-semibold">
                        {
                          formik.values.preconversationConfig.fields[index]
                            ?.label
                        }
                        <div className="ag-flex ag-gap-3">
                          <Trash
                            size={20}
                            className="ag-text-red-600"
                            onClick={() => remove(index)}
                          />
                          {expandedCards[index] ? (
                            <MinusCirlce
                              size="20"
                              onClick={() => toggleCard(index)}
                              className="cursor-pointer"
                            />
                          ) : (
                            <AddCircle
                              size="20"
                              onClick={() => toggleCard(index)}
                              className="cursor-pointer"
                            />
                          )}
                        </div>
                      </div>
                      {expandedCards[index] && (
                        <>
                          <Divider className="ag-mt-3" />
                          <div className="ag-flex ag-mt-3 ag-justify-between ag-gap-3">
                            <div className="ag-flex-1">
                              <label className="ag-block ag-mb-2 ag-font-medium">
                                Type
                              </label>
                              <SelectInput
                                name={`preconversationConfig.fields.${index}.type`}
                                options={Object.values(EPreconversationType)}
                                value={field.type}
                                onChange={(selected) => {
                                  formik.setFieldValue(
                                    `preconversationConfig.fields.${index}.type`,
                                    selected
                                  );
                                }}
                                placeholder="Choose type"
                                optionsWrapperClassName="!ag-bg-white !ag-border-neutral-200 ag-left-0 ag-h-[150px]"
                                className="ag-h-10 !ag-rounded-[10px] !ag-bg-neutral-50 !ag-border-neutral-200 !ag-text-neutral-700 !ag-w-full "
                                wrapperClassName="!ag-bg-white !ag-text-neutral-700 !ag-border-neutral-200 !ag-w-full"
                                optionsClassName="ag-text-neutral-700 ag-border-neutral-200 ag-text-sm ag-py-1 hover:ag-bg-neutral-200/80 dark:hover:ag-bg-[#262626]/80"
                              />
                            </div>
                            <div className="ag-mb-4 ag-flex-1">
                              <label className="ag-block ag-mb-2 ag-font-medium">
                                Label
                              </label>
                              <FormInput
                                className="ag-border ag-border-neutral-300 ag-py-1 ag-px-2 ag-rounded-md ag-text-sm ag-w-full focus:ag-border-primary-800"
                                placeholder="Label"
                                onBlur={formik.handleBlur}
                                onChange={(e) => {
                                  const newLabel = e.target.value;
                                  formik.setFieldValue(
                                    `preconversationConfig.fields.${index}.label`,
                                    newLabel
                                  );

                                  const generatedName =
                                    generateNameFromLabel(newLabel);
                                  formik.setFieldValue(
                                    `preconversationConfig.fields.${index}.name`,
                                    generatedName
                                  );
                                }}
                                value={field.label}
                                name={`preconversationConfig.fields.${index}.label`}
                                formik={formik}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )
                )}
              </div>
            </>
          )}
        </FieldArray>
      </FormikProvider>
    </div>
  );
};
